/**
 * WP 2.5 — kayda bağlı ortak yetenekler: etiketler, kullanıcıya özel favoriler, tek seferlik hatırlatıcılar, dosya ekleri.
 * Bu modül SAF'tır (Node/tarayıcı API'si yok; yalnız `Intl` ve `TextDecoder`): şemalar, normalizasyon, durum makinesi,
 * yerel zaman → UTC, dosya adı güvenliği ve içerik türü (magic byte) tespiti. Sözleşme: `docs/spec/castinflow/record-extras.md`.
 */
import { z } from 'zod';
const UUID = z.guid();
/** İstemci idempotency belirteci (oluşturma uçları): aynı belirteçle ikinci istek aynı satırı döndürür. */
export const clientTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);
/** Görünmez/yön denetim karakterleri (sahtecilik) ve C0/C1 denetim karakterleri (CR/LF dâhil) */
export function hasUnsafeChar(s) {
    for (const ch of s) {
        const c = ch.codePointAt(0) ?? 0;
        if (c < 32 || (c >= 127 && c < 160))
            return true;
        if ((c >= 0x200b && c <= 0x200f) ||
            (c >= 0x202a && c <= 0x202e) ||
            (c >= 0x2066 && c <= 0x2069))
            return true;
        if (c === 0xfeff)
            return true;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Etiketler
// ---------------------------------------------------------------------------
export const TAG_COLORS = [
    'gray',
    'red',
    'orange',
    'yellow',
    'green',
    'teal',
    'blue',
    'indigo',
    'purple',
    'pink',
];
export const TAG_NAME_MAX = 60;
/** Toplu etiket işleminde kayıt ve etiket üst sınırları */
export const TAG_BULK_MAX_RECORDS = 200;
export const TAG_BULK_MAX_TAGS = 20;
/** Görünen ad: NFC, kenar boşluğu kırpılır, iç boşluklar teke indirilir. Türkçe karakterler korunur (ASCII'ye indirgeme yok). */
export function cleanTagName(raw) {
    return raw.normalize('NFC').trim().replace(/\s+/gu, ' ');
}
/** Benzersizlik anahtarı: temiz ad + Türkçe küçük harf (I → ı, İ → i). "Müşteri" ile " müşteri " aynı etikettir. */
export function normalizeTagName(raw) {
    return cleanTagName(raw).toLocaleLowerCase('tr-TR');
}
export const tagNameSchema = z
    .string()
    .max(512)
    // Denetim/yön karakterleri TEMİZLENMEDEN önce reddedilir (satır sonu sessizce boşluğa çevrilip kabul edilmez)
    .refine((s) => !hasUnsafeChar(s), { message: 'Etiket adı denetim karakteri içeremez' })
    .transform(cleanTagName)
    .refine((s) => s.length >= 1 && s.length <= TAG_NAME_MAX, {
    message: `Etiket adı 1–${TAG_NAME_MAX} karakter olmalı`,
});
export const createTagInputSchema = z
    .object({ name: tagNameSchema, color: z.enum(TAG_COLORS).default('gray') })
    .strict();
export const updateTagInputSchema = z
    .object({ name: tagNameSchema.optional(), color: z.enum(TAG_COLORS).optional() })
    .strict()
    .refine((v) => v.name !== undefined || v.color !== undefined, { message: 'Değişiklik yok' });
/** Kayda etiket: mevcut etiket kimliği veya ad (yoksa oluşturulur — kayıt yazma yetkisi yeterli) */
export const addRecordTagInputSchema = z.union([
    z.object({ tagId: UUID }).strict(),
    z.object({ name: tagNameSchema, color: z.enum(TAG_COLORS).optional() }).strict(),
]);
export const bulkTagInputSchema = z
    .object({
    recordIds: z.array(UUID).min(1).max(TAG_BULK_MAX_RECORDS),
    add: z.array(UUID).max(TAG_BULK_MAX_TAGS).default([]),
    remove: z.array(UUID).max(TAG_BULK_MAX_TAGS).default([]),
})
    .strict()
    .refine((v) => v.add.length + v.remove.length > 0, {
    message: 'Eklenecek veya çıkarılacak etiket yok',
})
    .refine((v) => !v.add.some((t) => v.remove.includes(t)), {
    message: 'Aynı etiket hem eklenip hem çıkarılamaz',
});
// ---------------------------------------------------------------------------
// Hatırlatıcılar
// ---------------------------------------------------------------------------
export const REMINDER_STATUSES = [
    'scheduled',
    'triggered',
    'completed',
    'dismissed',
    'cancelled',
];
export const REMINDER_ACTIONS = ['complete', 'dismiss', 'snooze', 'cancel'];
/**
 * Tek durum makinesi (uygulama ve testler aynı tabloyu kullanır). Aynı eylemin tekrarı `noop` (idempotent);
 * kapanmış bir hatırlatıcıya farklı eylem `conflict`. Snooze'un idempotentliği ayrıca yeni vakte göre değerlendirilir.
 */
export function reminderTransition(from, action) {
    const open = from === 'scheduled' || from === 'triggered';
    const closeTo = (to) => open ? { kind: 'apply', to } : from === to ? { kind: 'noop' } : { kind: 'conflict' };
    switch (action) {
        case 'complete':
            return closeTo('completed');
        case 'dismiss':
            return closeTo('dismissed');
        case 'cancel':
            return closeTo('cancelled');
        case 'snooze':
            return open ? { kind: 'apply', to: 'scheduled' } : { kind: 'conflict' };
    }
}
/** IANA saat dilimi geçerli mi (çalışma zamanının ICU verisiyle) */
export function isValidTimeZone(tz) {
    if (!/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/.test(tz) || tz.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
const formatters = new Map();
function partsIn(instantMs, tz) {
    let f = formatters.get(tz);
    if (f === undefined) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(tz, f);
    }
    const parts = f.formatToParts(new Date(instantMs));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    return {
        y: get('year'),
        mo: get('month'),
        d: get('day'),
        h: get('hour'),
        mi: get('minute'),
        s: get('second'),
    };
}
const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
/**
 * Saat dilimli yerel zamanı UTC anına çevirir. DST kuralları (ürün kararı):
 *  - Yaz saatine geçişte ATLANAN yerel saat (ör. Avrupa'da 02:30) yoktur → `nonexistent` (422; istemci başka saat seçer).
 *  - Kış saatine dönüşte İKİ KEZ yaşanan yerel saat → daha ERKEN an seçilir, `ambiguous: true` döner.
 */
export function zonedLocalToUtc(local, tz) {
    const m = LOCAL_RE.exec(local);
    if (!m || !isValidTimeZone(tz))
        return { ok: false, reason: 'invalid' };
    const want = {
        y: Number(m[1]),
        mo: Number(m[2]),
        d: Number(m[3]),
        h: Number(m[4]),
        mi: Number(m[5]),
        s: Number(m[6] ?? '0'),
    };
    if (want.h > 23 || want.mi > 59 || want.s > 59)
        return { ok: false, reason: 'invalid' };
    const guess = Date.UTC(want.y, want.mo - 1, want.d, want.h, want.mi, want.s);
    const check = new Date(guess);
    if (check.getUTCMonth() !== want.mo - 1 || check.getUTCDate() !== want.d)
        return { ok: false, reason: 'invalid' };
    const offsetAt = (ms) => {
        const p = partsIn(ms, tz);
        return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - ms;
    };
    const offsets = new Set([
        offsetAt(guess - 86_400_000),
        offsetAt(guess),
        offsetAt(guess + 86_400_000),
    ]);
    const hits = new Set();
    for (const o of offsets) {
        const t = guess - o;
        const p = partsIn(t, tz);
        if (p.y === want.y &&
            p.mo === want.mo &&
            p.d === want.d &&
            p.h === want.h &&
            p.mi === want.mi &&
            p.s === want.s)
            hits.add(t);
    }
    const sorted = [...hits].sort((a, b) => a - b);
    const first = sorted[0];
    if (first === undefined)
        return { ok: false, reason: 'nonexistent' };
    return { ok: true, utc: new Date(first), ambiguous: sorted.length > 1 };
}
/** Ofsetli ISO 8601 an (ör. `2026-10-25T09:00:00+03:00` veya `…Z`); ofsetsiz değer reddedilir. */
export const OFFSET_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
/** `dueAt` (ofsetli an) veya `dueLocal` + `timeZone` → UTC an. İkisinden yalnız biri verilmeli. */
export function resolveDue(input) {
    if (!isValidTimeZone(input.timeZone))
        return { ok: false, message: 'Geçersiz saat dilimi' };
    if ((input.dueAt === undefined) === (input.dueLocal === undefined))
        return { ok: false, message: 'dueAt veya dueLocal alanlarından yalnız biri verilmeli' };
    if (input.dueAt !== undefined) {
        if (!OFFSET_ISO_RE.test(input.dueAt))
            return { ok: false, message: 'dueAt açık ofsetli ISO 8601 olmalı (ör. …+03:00 veya …Z)' };
        const d = new Date(input.dueAt);
        if (Number.isNaN(d.getTime()))
            return { ok: false, message: 'Geçersiz zaman' };
        return { ok: true, dueAt: d, ambiguous: false };
    }
    const r = zonedLocalToUtc(input.dueLocal ?? '', input.timeZone);
    if (!r.ok)
        return {
            ok: false,
            message: r.reason === 'nonexistent'
                ? 'Bu yerel saat, saat dilimindeki yaz saati geçişi nedeniyle yok'
                : 'Yerel zaman YYYY-MM-DDTHH:mm biçiminde geçerli bir değer olmalı',
        };
    return { ok: true, dueAt: r.utc, ambiguous: r.ambiguous };
}
const titleSchema = z
    .string()
    .max(1000)
    .transform((s) => s.normalize('NFC').trim())
    .refine((s) => s.length >= 1 && s.length <= 200, 'Başlık 1–200 karakter olmalı')
    .refine((s) => !hasUnsafeChar(s), 'Başlık denetim karakteri içeremez');
export const createReminderInputSchema = z
    .object({
    title: titleSchema,
    note: z.string().max(1000).optional(),
    dueAt: z.string().max(40).optional(),
    dueLocal: z.string().max(40).optional(),
    timeZone: z.string().max(64),
    /** Başka kullanıcıya atama: `reminders.assign` yeteneği gerekir; varsayılan çağıranın kendisi. */
    recipientUserId: UUID.optional(),
    clientToken: clientTokenSchema.optional(),
})
    .strict();
export const snoozeReminderInputSchema = z
    .object({
    dueAt: z.string().max(40).optional(),
    dueLocal: z.string().max(40).optional(),
    timeZone: z.string().max(64),
})
    .strict();
/** Tetikleme işinin idempotency anahtarı: vakit her ertelemede sürüm alır → eski iş yeni vakti tetikleyemez. */
export function reminderDeliveryKey(reminderId, dueVersion) {
    return `reminder:${reminderId}:v${dueVersion}`;
}
// ---------------------------------------------------------------------------
// Dosya ekleri
// ---------------------------------------------------------------------------
export const ATTACHMENT_STATUSES = [
    'pending',
    'uploaded',
    'active',
    'deleted',
    'expired',
    'rejected',
];
export const SCAN_STATUSES = ['pending', 'clean', 'infected', 'failed'];
/**
 * İzinli dosya türleri: uzantı → beklenen içerik türü. HTML/SVG/betik/çalıştırılabilir dosyalar kabul edilmez. Liste yalnız
 * KODDA genişler (tür kararı ve gerekçeleri: `docs/security/m6-file-types-and-scanning.md`); çalışma alanı politikası listeyi
 * yalnız DARALTIR (`tenant_settings.file_blocked_extensions`).
 */
export const FILE_TYPES = {
    pdf: { mime: 'application/pdf', family: 'pdf' },
    png: { mime: 'image/png', family: 'png' },
    jpg: { mime: 'image/jpeg', family: 'jpeg' },
    jpeg: { mime: 'image/jpeg', family: 'jpeg' },
    gif: { mime: 'image/gif', family: 'gif' },
    webp: { mime: 'image/webp', family: 'webp' },
    txt: { mime: 'text/plain', family: 'text' },
    csv: { mime: 'text/csv', family: 'text' },
    md: { mime: 'text/markdown', family: 'text' },
    docx: {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        family: 'zip',
    },
    xlsx: {
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        family: 'zip',
    },
    pptx: {
        mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        family: 'zip',
    },
    odt: { mime: 'application/vnd.oasis.opendocument.text', family: 'zip' },
    ods: { mime: 'application/vnd.oasis.opendocument.spreadsheet', family: 'zip' },
    zip: { mime: 'application/zip', family: 'zip' },
    doc: { mime: 'application/msword', family: 'ole' },
    xls: { mime: 'application/vnd.ms-excel', family: 'ole' },
    ppt: { mime: 'application/vnd.ms-powerpoint', family: 'ole' },
    // Ses (M3 WP 3.10 sesli not ve not eki). Uzantı yalnız ses türüne eşlenir (ör. `.mp4` video uzantısı izinli DEĞİL);
    // içerik ailesi yine magic byte ile doğrulanır.
    webm: { mime: 'audio/webm', family: 'webm' },
    weba: { mime: 'audio/webm', family: 'webm' },
    ogg: { mime: 'audio/ogg', family: 'ogg' },
    oga: { mime: 'audio/ogg', family: 'ogg' },
    opus: { mime: 'audio/ogg', family: 'ogg' },
    m4a: { mime: 'audio/mp4', family: 'mp4' },
    mp3: { mime: 'audio/mpeg', family: 'mp3' },
    wav: { mime: 'audio/wav', family: 'wav' },
    // M6-H5: içerik yapısı denetlenir (`BEGIN:VCALENDAR` / RFC 5322 başlık bloğu); sunucu bu dosyaları açmaz, yorumlamaz.
    ics: { mime: 'text/calendar', family: 'ics' },
    eml: { mime: 'message/rfc822', family: 'eml' },
};
/** Ses içerik türü mü (sunucunun tespit ettiği tür; istemci beyanı değil) */
export function isAudioMime(mime) {
    return typeof mime === 'string' && mime.startsWith('audio/');
}
/**
 * Dosyanın kabul edilmeme nedeni (M6-H5). Yükleme akışı ve gelen e-posta ekleri AYNI kodları ve aynı cümleleri kullanır
 * (`FILE_BLOCK_REASON_LABELS`): kullanıcı iki yerde farklı anlatım görmez.
 */
export const FILE_BLOCK_REASONS = [
    'type_not_allowed',
    'executable',
    'content_mismatch',
    'encrypted_archive',
    'workspace_policy',
    'scan_limits',
];
export const FILE_BLOCK_REASON_LABELS = {
    type_not_allowed: 'Bu dosya türü desteklenmiyor.',
    executable: 'Çalıştırılabilir dosya ya da betik kabul edilmiyor.',
    content_mismatch: 'Dosyanın içeriği uzantısıyla uyuşmuyor.',
    encrypted_archive: 'Parola korumalı arşiv ya da belge taranamadığı için kabul edilmiyor.',
    workspace_policy: 'Bu dosya türü çalışma alanının dosya politikasıyla kapatılmış.',
    scan_limits: 'Arşivin açılmış içeriği güvenlik taramasının sınırlarını aştığı için tam taranamaz; kabul edilmiyor.',
};
/** Çalıştırılabilir/betik uzantıları (izin listesinde yoklar; gerekçe "tür desteklenmiyor" yerine açıkça yazılsın diye) */
export const EXECUTABLE_EXTENSIONS = new Set([
    'exe',
    'dll',
    'com',
    'bat',
    'cmd',
    'msi',
    'msp',
    'scr',
    'pif',
    'cpl',
    'ps1',
    'psm1',
    'vbs',
    'vbe',
    'js',
    'jse',
    'mjs',
    'wsf',
    'wsh',
    'hta',
    'jar',
    'class',
    'sh',
    'bash',
    'zsh',
    'app',
    'apk',
    'deb',
    'rpm',
    'dmg',
    'lnk',
    'reg',
    'py',
    'pl',
    'rb',
    'php',
    'so',
    'dylib',
    'elf',
]);
/** Tarayıcıda çalışabilen işaretleme uzantıları (bilerek desteklenmez) */
const ACTIVE_CONTENT_EXTENSIONS = new Set([
    'html',
    'htm',
    'xhtml',
    'shtml',
    'mht',
    'mhtml',
    'svg',
    'svgz',
    'xml',
    'xsl',
    'xslt',
]);
/** İçi güvenle taranamayan/denetlenemeyen arşiv biçimleri (bilerek desteklenmez; zip desteklenir) */
const UNSUPPORTED_ARCHIVE_EXTENSIONS = new Set([
    '7z',
    'rar',
    'tar',
    'gz',
    'tgz',
    'bz2',
    'xz',
    'cab',
    'iso',
    'arj',
    'lzh',
]);
/** "Tür desteklenmiyor" gerekçesinin uzantıya özgü açıklaması (yoksa `null`) */
export function fileTypeHint(ext) {
    const e = (ext ?? '').toLowerCase();
    if (ACTIVE_CONTENT_EXTENSIONS.has(e))
        return 'HTML, SVG ve XML dosyaları tarayıcıda çalışabilen içerik taşıyabildiği için saklanmaz.';
    if (UNSUPPORTED_ARCHIVE_EXTENSIONS.has(e))
        return 'Bu arşiv biçimi desteklenmiyor; zip kullanabilirsiniz.';
    return null;
}
/** Dosya adının uzantısı (küçük harf; yoksa `null`) */
export function fileExtension(name) {
    if (typeof name !== 'string')
        return null;
    const dot = name.lastIndexOf('.');
    if (dot <= 0 || dot === name.length - 1)
        return null;
    return name.slice(dot + 1).toLowerCase();
}
/** Kullanıcıya gösterilen engellenme metni: gerekçe cümlesi + (yalnız "tür desteklenmiyor"da) uzantıya özgü açıklama */
export function fileBlockMessage(reason, fileName) {
    const hint = reason === 'type_not_allowed' ? fileTypeHint(fileExtension(fileName)) : null;
    return hint === null
        ? FILE_BLOCK_REASON_LABELS[reason]
        : `${FILE_BLOCK_REASON_LABELS[reason]} ${hint}`;
}
/** Çalışma alanının kapattığı uzantılar: küçük harf, en çok 64 öğe (DB CHECK ile aynı kural) */
export const FILE_BLOCKED_EXTENSIONS_MAX = 64;
export const fileBlockedExtensionsSchema = z
    .array(z.string().regex(/^[a-z0-9]{1,10}$/))
    .max(FILE_BLOCKED_EXTENSIONS_MAX);
/**
 * Uzantının tür kararı (ad düzeyi; içerik ayrıca `fileContentVerdict` ile). Çalışma alanı listesi yalnız daraltır: izin
 * listesinde olmayan bir uzantıyı `blocked` listesine yazmamak onu AÇMAZ (genişletme yolu yoktur).
 */
export function fileExtensionVerdict(ext, blocked = []) {
    if (ext !== null && EXECUTABLE_EXTENSIONS.has(ext))
        return { ok: false, reason: 'executable' };
    if (ext === null || FILE_TYPES[ext] === undefined)
        return { ok: false, reason: 'type_not_allowed' };
    if (blocked.includes(ext))
        return { ok: false, reason: 'workspace_policy' };
    return { ok: true };
}
/**
 * Dosya adı yalnız SUNUM meta verisidir; depolama anahtarı ondan türetilmez. Yine de kayıt ve `Content-Disposition` güvenliği
 * için: NFC, kenar boşluğu kırpılır; yol ayırıcı (`/`, `\`), `..`, sürücü öneki, gizli dosya (`.` ile başlayan), denetim /
 * görünmez / yön karakterleri (CR/LF başlık enjeksiyonu dâhil) reddedilir; uzunluk ≤ 255; uzantı izin listesinde olmalı.
 */
export function checkFileName(raw) {
    if (typeof raw !== 'string')
        return { ok: false, reason: 'Dosya adı metin olmalı' };
    const name = raw.normalize('NFC').trim();
    if (name.length === 0 || name.length > 255)
        return { ok: false, reason: 'Dosya adı 1–255 karakter olmalı' };
    if (hasUnsafeChar(name))
        return { ok: false, reason: 'Dosya adı denetim karakteri içeremez' };
    if (/[/\\]/.test(name) || /^[A-Za-z]:/.test(name))
        return { ok: false, reason: 'Dosya adı yol içeremez' };
    if (name.startsWith('.'))
        return { ok: false, reason: 'Geçersiz dosya adı' };
    if (/["<>|*?:]/.test(name))
        return { ok: false, reason: 'Dosya adı özel karakter içeremez' };
    const dot = name.lastIndexOf('.');
    if (dot <= 0 || dot === name.length - 1)
        return { ok: false, reason: 'Dosya uzantısı gerekli' };
    const ext = name.slice(dot + 1).toLowerCase();
    const verdict = fileExtensionVerdict(ext);
    if (!verdict.ok)
        return { ok: false, reason: fileBlockMessage(verdict.reason, name), code: verdict.reason };
    return { ok: true, name, ext };
}
function startsWith(b, sig, offset = 0) {
    if (b.length < offset + sig.length)
        return false;
    return sig.every((v, i) => b[offset + i] === v);
}
function hasAscii(b, word) {
    const sig = Array.from(new TextEncoder().encode(word));
    for (let i = 0; i + sig.length <= b.length; i += 1)
        if (startsWith(b, sig, i))
            return true;
    return false;
}
/** MPEG ses çerçeve eşlemesi, yalnız Layer III (`FF FB`, `FF F3`, `FF E3` …); UTF-16 BOM (`FF FE`) eşleşmez. */
function isMp3LayerIIIFrame(b) {
    const b0 = b[0];
    const b1 = b[1];
    return b.length >= 4 && b0 === 0xff && b1 !== undefined && (b1 & 0xe6) === 0xe2;
}
/** Geçerli UTF-8 ve NUL içermeyen metin mi */
function isUtf8Text(b) {
    if (b.includes(0))
        return false;
    try {
        new TextDecoder('utf-8', { fatal: true }).decode(b);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * İçerik (magic byte) ailesi. İstemcinin bildirdiği MIME'a güvenilmez; kabul, uzantının ailesi ile içeriğin ailesi
 * EŞLEŞİRSE verilir. Tanınmayan ikili içerik, çalıştırılabilir dosya/betik ve tarayıcıda çalışabilen işaretleme `null`.
 */
export function sniffFileFamily(b) {
    if (startsWith(b, [0x25, 0x50, 0x44, 0x46, 0x2d]))
        return 'pdf';
    if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        return 'png';
    if (startsWith(b, [0xff, 0xd8, 0xff]))
        return 'jpeg';
    if (startsWith(b, [0x47, 0x49, 0x46, 0x38]))
        return 'gif';
    if (startsWith(b, [0x52, 0x49, 0x46, 0x46]) && startsWith(b, [0x57, 0x45, 0x42, 0x50], 8))
        return 'webp';
    // Ses kapları: WAV (RIFF…WAVE), Ogg, WebM (EBML + DocType "webm"), ISO BMFF (`ftyp`), MP3 (ID3 ya da Layer III çerçevesi)
    if (startsWith(b, [0x52, 0x49, 0x46, 0x46]) && startsWith(b, [0x57, 0x41, 0x56, 0x45], 8))
        return 'wav';
    // İmzadan sonraki NUL baytı (sürüm/kutu boyu) metin dosyasının yanlışlıkla ses sayılmasını önler
    if (startsWith(b, [0x4f, 0x67, 0x67, 0x53, 0x00]))
        return 'ogg';
    if (startsWith(b, [0x1a, 0x45, 0xdf, 0xa3]) && hasAscii(b.subarray(0, 64), 'webm'))
        return 'webm';
    if (b[0] === 0x00 && startsWith(b, [0x66, 0x74, 0x79, 0x70], 4))
        return 'mp4';
    if ((startsWith(b, [0x49, 0x44, 0x33]) && [2, 3, 4].includes(b[3] ?? -1) && b[4] === 0x00) ||
        isMp3LayerIIIFrame(b))
        return 'mp3';
    if (startsWith(b, [0x50, 0x4b, 0x03, 0x04]) || startsWith(b, [0x50, 0x4b, 0x05, 0x06]))
        return 'zip';
    if (startsWith(b, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]))
        return 'ole';
    // Çalıştırılabilir/betik imzaları (MZ, ELF, Mach-O, #!) metin sayılmaz
    if (isExecutableContent(b))
        return null;
    const utf8 = isUtf8Text(b);
    // Takvim satırları (`AD:değer`) başlık satırına benzer: takvim denetimi ileti denetiminden ÖNCE
    if (utf8 && isIcsText(b))
        return 'ics';
    // E-posta iletisi (başlık bloğu 7 bit/UTF-8; gövde 8 bit olabilir) düz metin denetiminden önce
    if (isEmlContent(b))
        return 'eml';
    if (utf8) {
        const head = new TextDecoder().decode(b.subarray(0, 512)).trimStart().toLowerCase();
        if (head.startsWith('<!doctype html') ||
            head.startsWith('<html') ||
            head.startsWith('<svg') ||
            head.startsWith('<?xml') ||
            head.startsWith('<script'))
            return null;
        return 'text';
    }
    return null;
}
/** Çalıştırılabilir/betik imzası: PE (MZ), ELF, Mach-O (tek/çok mimarili), `#!` */
export function isExecutableContent(b) {
    return (startsWith(b, [0x4d, 0x5a]) ||
        startsWith(b, [0x7f, 0x45, 0x4c, 0x46]) ||
        startsWith(b, [0x23, 0x21]) ||
        startsWith(b, [0xfe, 0xed, 0xfa, 0xce]) ||
        startsWith(b, [0xfe, 0xed, 0xfa, 0xcf]) ||
        startsWith(b, [0xce, 0xfa, 0xed, 0xfe]) ||
        startsWith(b, [0xcf, 0xfa, 0xed, 0xfe]) ||
        startsWith(b, [0xca, 0xfe, 0xba, 0xbe]));
}
/** Baştaki bayt sırası işaretini (U+FEFF) atar */
const BOM_CODE = 0xfeff;
const stripBom = (s) => (s.charCodeAt(0) === BOM_CODE ? s.slice(1) : s);
/** iCalendar (RFC 5545): (BOM/boşluktan sonra) `BEGIN:VCALENDAR` satırıyla başlar ve `END:VCALENDAR` içerir */
function isIcsText(b) {
    // Önce yalnız baş kısım: takvim olmayan büyük metin dosyasının tamamı ikinci kez çözülmez
    const head = stripBom(new TextDecoder().decode(b.subarray(0, 256)));
    if (!/^\s*BEGIN:VCALENDAR[ \t]*\r?\n/i.test(head))
        return false;
    return /\r?\nEND:VCALENDAR[ \t]*(\r?\n)*$/i.test(new TextDecoder().decode(b));
}
const EML_ANCHOR_HEADERS = new Set([
    'from',
    'date',
    'received',
    'return-path',
    'message-id',
    'mime-version',
]);
/**
 * E-posta iletisi (RFC 5322): dosya bir başlık bloğuyla başlar (`Ad: değer`, devam satırları boşlukla), blok boş satırla ya da
 * dosya sonuyla biter; en az iki başlık ve bunlardan en az biri ileti başlığı (From, Date, Received, Return-Path, Message-ID,
 * MIME-Version). NUL içeren içerik ve başlığı geçerli UTF-8 olmayan içerik ileti sayılmaz. Gövde YORUMLANMAZ (ekler açılmaz).
 */
function isEmlContent(b) {
    if (b.includes(0))
        return false;
    const limit = Math.min(b.length, 64 * 1024);
    let end = -1;
    for (let i = 0; i + 1 < limit; i += 1) {
        if (b[i] === 0x0a && (b[i + 1] === 0x0a || (b[i + 1] === 0x0d && b[i + 2] === 0x0a))) {
            end = i;
            break;
        }
    }
    if (end === -1 && b.length > limit)
        return false;
    let head;
    try {
        head = new TextDecoder('utf-8', { fatal: true }).decode(b.subarray(0, end === -1 ? b.length : end));
    }
    catch {
        return false;
    }
    const names = [];
    for (const line of stripBom(head).split(/\r?\n/)) {
        if (line === '')
            continue;
        if (/^[ \t]/.test(line)) {
            // Devam satırı yalnız bir başlıktan sonra gelebilir
            if (names.length === 0)
                return false;
            continue;
        }
        const m = /^([A-Za-z][A-Za-z0-9-]{0,76}):/.exec(line);
        if (m?.[1] === undefined)
            return false;
        names.push(m[1].toLowerCase());
    }
    return names.length >= 2 && names.some((n) => EML_ANCHOR_HEADERS.has(n));
}
const u16 = (b, o) => (b[o] ?? 0) | ((b[o + 1] ?? 0) << 8);
const u32 = (b, o) => ((b[o] ?? 0) | ((b[o + 1] ?? 0) << 8) | ((b[o + 2] ?? 0) << 16) | ((b[o + 3] ?? 0) << 24)) >>> 0;
/**
 * Zip arşivinde parola korumalı (şifreli) girdi var mı. Arşiv AÇILMAZ; yalnız üst veriler okunur: merkezi dizindeki her
 * girdinin genel amaçlı bayrağı (bit 0 = şifreli, bit 6 = güçlü şifreleme, bit 13 = şifreli merkezi dizin) ve ilk yerel
 * başlık. Şifreli girdinin içeriği taranamaz; tarayıcı böyle bir arşivi "temiz" bildirebilir — bu yüzden kabul edilmez.
 */
export function zipHasEncryptedEntry(b) {
    const ENCRYPTED = 0x0001 | 0x0040 | 0x2000;
    // İlk yerel başlık yalnız gerçekten başlık gibi görünüyorsa okunur (sürüm alanı makul, başlık arabelleğe sığıyor);
    // aksi hâlde `PK\x03\x04` ile başlayan rastgele içerik yanlışlıkla "şifreli" sayılırdı.
    if (startsWith(b, [0x50, 0x4b, 0x03, 0x04]) &&
        u16(b, 4) <= 100 &&
        30 + u16(b, 26) + u16(b, 28) <= b.length &&
        (u16(b, 6) & ENCRYPTED) !== 0)
        return true;
    // Merkezi dizin sonu kaydı (EOCD): sondan geriye, en çok 64 KB yorum + 22 bayt
    const min = Math.max(0, b.length - (0xffff + 22));
    let eocd = -1;
    for (let i = b.length - 22; i >= min; i -= 1) {
        if (startsWith(b, [0x50, 0x4b, 0x05, 0x06], i)) {
            eocd = i;
            break;
        }
    }
    if (eocd === -1)
        return false;
    const count = u16(b, eocd + 10);
    let p = u32(b, eocd + 16);
    for (let n = 0; n < count && p + 46 <= b.length; n += 1) {
        if (!startsWith(b, [0x50, 0x4b, 0x01, 0x02], p))
            break;
        if ((u16(b, p + 8) & ENCRYPTED) !== 0)
            return true;
        p += 46 + u16(b, p + 28) + u16(b, p + 30) + u16(b, p + 32);
    }
    return false;
}
/**
 * Tarayıcı sınırları (clamd `MaxFileSize` / `MaxScanSize` / `MaxFiles`; infra/staging/compose.infra.yml ile AYNI — yapı testi
 * eşitliği denetler). ClamAV'da "M" = MiB.
 */
export const SCANNER_LIMITS = {
    memberBytes: 100 * 1024 * 1024,
    totalBytes: 400 * 1024 * 1024,
    files: 10_000,
};
/**
 * Zip arşivinin AÇILMIŞ içeriği tarayıcı sınırlarını aşıyor mu (tarama politikası, M6 kapanışı). Arşiv AÇILMAZ; yalnız
 * merkezi dizindeki bildirilen açılmış boyutlar okunur (`zipHasEncryptedEntry` ile aynı üst veri yürüyüşü). Gerekçe
 * (staging clamd 1.5.4 ile ölçüldü, 2026-09-30): sınırı aşan arşiv ÜYESİ `AlertExceedsMax` açıkken bile uyarısız kısmen
 * taranır ve "OK" döner — büyük, sıkıştırılabilir dolgunun ardına saklanan içerik denetlenmez. ZIP64 işaretli boyut/sayı
 * (0xFFFFFFFF / 0xFFFF) sınır dışı sayılır. Merkezi dizin bulunamazsa karar verilmez (false; tarayıcı dosyayı tarar).
 * İç içe arşivde İÇ üyenin boyutu burada görülmez (derinlik sınırını tarayıcı bildirir): bilinen sınır.
 */
export function zipExceedsScanLimits(b, limits = SCANNER_LIMITS) {
    const min = Math.max(0, b.length - (0xffff + 22));
    let eocd = -1;
    for (let i = b.length - 22; i >= min; i -= 1) {
        if (startsWith(b, [0x50, 0x4b, 0x05, 0x06], i)) {
            eocd = i;
            break;
        }
    }
    if (eocd === -1)
        return false;
    const count = u16(b, eocd + 10);
    if (count === 0xffff || count > limits.files)
        return true;
    let p = u32(b, eocd + 16);
    let total = 0;
    for (let n = 0; n < count && p + 46 <= b.length; n += 1) {
        if (!startsWith(b, [0x50, 0x4b, 0x01, 0x02], p))
            break;
        const size = u32(b, p + 24);
        if (size === 0xffffffff || size > limits.memberBytes)
            return true;
        total += size;
        if (total > limits.totalBytes)
            return true;
        p += 46 + u16(b, p + 28) + u16(b, p + 30) + u16(b, p + 32);
    }
    return false;
}
/** UTF-16LE metin arama (OLE akış adları) */
function hasUtf16Le(b, word) {
    const sig = [];
    for (const ch of word)
        sig.push(ch.charCodeAt(0) & 0xff, 0x00);
    for (let i = 0; i + sig.length <= b.length; i += 1)
        if (startsWith(b, sig, i))
            return true;
    return false;
}
/** Parola korumalı Office Open XML belgesi zip DEĞİL, içinde `EncryptedPackage` akışı olan OLE kabıdır */
const OOXML_EXTENSIONS = new Set(['docx', 'xlsx', 'pptx']);
/** Uzantı ailesi içerik ailesini kabul eder mi: aynı aile; düz metin uzantısı takvim/ileti biçimli metni de kabul eder */
export function fileFamilyAccepts(expected, sniffed) {
    return expected === sniffed || (expected === 'text' && (sniffed === 'ics' || sniffed === 'eml'));
}
/**
 * İçerik kararı (yükleme ve gelen e-posta eki AYNI kuralı kullanır): uzantı izin listesinde, içerik çalıştırılabilir değil,
 * arşiv/belge parola korumalı değil ve içerik ailesi uzantının ailesiyle uyumlu olmalı. İstemcinin bildirdiği tür karar vermez.
 */
export function fileContentVerdict(ext, b) {
    const type = FILE_TYPES[ext];
    if (type === undefined)
        return {
            ok: false,
            reason: EXECUTABLE_EXTENSIONS.has(ext) ? 'executable' : 'type_not_allowed',
        };
    if (isExecutableContent(b))
        return { ok: false, reason: 'executable' };
    const fam = sniffFileFamily(b);
    if (type.family === 'zip') {
        if (fam === 'zip' && zipHasEncryptedEntry(b))
            return { ok: false, reason: 'encrypted_archive' };
        if (fam === 'zip' && zipExceedsScanLimits(b))
            return { ok: false, reason: 'scan_limits' };
        if (fam === 'ole' && OOXML_EXTENSIONS.has(ext) && hasUtf16Le(b, 'EncryptedPackage'))
            return { ok: false, reason: 'encrypted_archive' };
    }
    if (fam === null || !fileFamilyAccepts(type.family, fam))
        return { ok: false, reason: 'content_mismatch' };
    return { ok: true, mime: type.mime, family: fam };
}
export const initiateUploadInputSchema = z
    .object({
    fileName: z.string().max(1024),
    size: z.number().int().positive(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/, 'SHA-256 onaltılık (küçük harf) olmalı'),
    /** İstemcinin bildirdiği tür: yalnız kayıt amaçlı, karar vermez */
    contentType: z.string().max(127).optional(),
    clientToken: clientTokenSchema.optional(),
})
    .strict();
export const ATTACHMENT_SCAN_ISSUES = ['encrypted', 'limits_exceeded'];
/** RFC 6266/5987 `Content-Disposition` (attachment): ASCII yedek ad + UTF-8 `filename*`. Başlık enjeksiyonu yapılamaz. */
export function contentDisposition(fileName) {
    const ascii = fileName
        .normalize('NFKD')
        .replace(/[^\x20-\x7e]/g, '')
        .replace(/["\\;%]/g, '_')
        .trim();
    const fallback = ascii.length > 0 ? ascii : 'dosya';
    const star = encodeURIComponent(fileName).replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
    return `attachment; filename="${fallback}"; filename*=UTF-8''${star}`;
}
//# sourceMappingURL=record-extras.js.map